import React, { useContext } from 'react';
import {
  LocaleType,
  TimezoneType,
  LocalizationService,
  tFunctionType,
} from 'app/services/localization';
import tx from 'app/services/tx';

// Re-exported for convenience
export { tFunctionType };

export interface ILocalizationState {
  locale: LocaleType;
  timezone: TimezoneType;
  fallbackTimezone: TimezoneType;
  keyMode: boolean;
  t: tFunctionType;
  tx: (name: string, data?: { [x: string]: React.ReactNode }) => React.ReactNode;
  formatDate: LocalizationService['formatDate'];
  formatDuration: LocalizationService['formatDuration'];
  fromNow: LocalizationService['fromNow'];
  fromOther: LocalizationService['fromOther'];
  // Also include isSameDay, because its result may depend on the current timezone.
  isSameDay: LocalizationService['isSameDay'];
}

export type ILocalizationMethods = Pick<
  LocalizationService,
  'setLocale' | 'setKeyMode' | 'setTimezone' | 'setPhrases' | 'setFallbackTimezone'
>;

export type ILocalizationContext = ILocalizationState & ILocalizationMethods;

const LocalizationContext = React.createContext<ILocalizationContext>(
  // Default instance for tests
  makeLocalizationContext(new LocalizationService(), {
    setLocale: () => { throw new Error('Not implemented'); },
    setKeyMode: () => { throw new Error('Not implemented'); },
    setTimezone: () => { throw new Error('Not implemented'); },
    setPhrases: () => { throw new Error('Not implemented'); },
    setFallbackTimezone: () => { throw new Error('Not implemented'); },
  }),
);

export class LocalizationProvider extends React.Component<
  { service: LocalizationService },
  { localizationContext: ILocalizationContext }
> {
  // These are just bound versions of the LocalizationService methods.
  public readonly localizationMethods: ILocalizationMethods = {
    setLocale: (...args) => this.props.service.setLocale(...args),
    setKeyMode: (...args) => this.props.service.setKeyMode(...args),
    setTimezone: (...args) => this.props.service.setTimezone(...args),
    setPhrases: (...args) => this.props.service.setPhrases(...args),
    setFallbackTimezone: (...args) => this.props.service.setFallbackTimezone(...args),
  };

  public state = {
    localizationContext: makeLocalizationContext(this.props.service, this.localizationMethods),
  };

  private unsubscribe?: () => void = undefined;

  public componentDidMount() {
    this.unsubscribe = this.props.service.subscribe(this.onLocalizationChange);
  }

  public componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  public render() {
    return (
      <LocalizationContext.Provider value={this.state.localizationContext}>
        {this.props.children}
      </LocalizationContext.Provider>
    );
  }

  private onLocalizationChange = () => {
    this.setState(({ localizationContext }, { service }) => ({
      localizationContext: makeLocalizationContext(service, this.localizationMethods),
    }));
  };
}

export const T: React.SFC<{
  children: (tFunction: LocalizationService['t']) => React.ReactNode,
}> = ({ children }) => (
  <I18n>{localizationContext =>
    children(localizationContext.t)
  }</I18n>
);

// Similar to <T>, but passes an object with several i18n methods, instead of only the t() function.
export const I18n: React.SFC<{
  children: (localizationContext: ILocalizationContext) => React.ReactNode,
}> = ({ children }) => (
  <LocalizationContext.Consumer>{localizationContext =>
    children(localizationContext)
  }</LocalizationContext.Consumer>
);

export const useI18n = (): ILocalizationContext => {
  const localizationContext = useContext(LocalizationContext);

  return localizationContext;
};

function makeLocalizationContext(
  localizationService: LocalizationService,
  methods: ILocalizationMethods,
): ILocalizationContext {
  // In addition to including the localization state like locale and timzone, create new bound
  // versions of each of the functions, so that they can be treated as immutable values and used
  // with PureComponent/reselect/etc.
  return {
    locale: localizationService.getLocale(),
    timezone: localizationService.getTimezone(),
    fallbackTimezone: localizationService.getFallbackTimezone(),
    keyMode: localizationService.getKeyMode(),
    // eslint-disable-next-line @engoo/engoo/literal-string-keys
    t: (...args) => localizationService.t(...args),
    // eslint-disable-next-line @engoo/engoo/literal-string-keys
    tx: (...args) => tx(localizationService, ...args),
    formatDate: (...args) => localizationService.formatDate(...args),
    formatDuration: (...args) => localizationService.formatDuration(...args),
    fromNow: (...args) => localizationService.fromNow(...args),
    fromOther: (...args) => localizationService.fromOther(...args),
    isSameDay: (...args) => localizationService.isSameDay(...args),
    ...methods,
  };
}
